import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ArquivoGerado, RelatorioAgenda, ResultadoGeracao, StatusJob } from '../../interfaces/relatorios-agenda.dto';
import { ehPdf, mensagemDoBlob, nomeDoArquivo } from '../../utils/baixar-arquivo.util';

const TEMPO_ESGOTADO = 'A Agenda não respondeu a tempo. Tente de novo ou use um período menor.';

/** Relatórios da Agenda pelo repasse do back: erros sobem com a mensagem da Agenda. */
@Injectable({ providedIn: 'root' })
export class RelatoriosAgendaService {
  private readonly apiURL = `${environment.apiUrl}/v${environment.apiVersion}/relatorios/agenda`;

  constructor(private http: HttpClient) {}

  async lista(): Promise<RelatorioAgenda[]> {
    try {
      const r = await firstValueFrom(this.http.get<RelatorioAgenda[]>(`${this.apiURL}/lista`));
      return Array.isArray(r) ? r : [];
    } catch (e: any) {
      throw new Error(mensagemJson(e, 'Não foi possível carregar os relatórios agora.'));
    }
  }

  async dados<T = any[]>(caminho: string): Promise<T> {
    try {
      return await firstValueFrom(this.http.get<T>(`${this.apiURL}/dados`, { params: new HttpParams().set('caminho', caminho) }));
    } catch (e: any) {
      throw new Error(mensagemJson(e, 'Erro ao carregar dados'));
    }
  }

  async gerar(id: string, corpo: Record<string, string>): Promise<ResultadoGeracao> {
    try {
      const r = await firstValueFrom(this.http.post(`${this.apiURL}/gerar/${encodeURIComponent(id)}`, corpo, {
        observe: 'response',
        responseType: 'blob',
      }));
      if (r.status === 202) {
        const job = JSON.parse(await (r.body as Blob).text());
        return { tipo: 'job', jobId: String(job.jobId) };
      }
      return { tipo: 'arquivo', arquivo: arquivoDa(r) };
    } catch (e: any) {
      throw new Error(await mensagemDoBlob(e, padraoDoErro(e, 'Falha ao gerar o relatório.')));
    }
  }

  async statusDoJob(jobId: string): Promise<StatusJob> {
    try {
      return await firstValueFrom(this.http.get<StatusJob>(`${this.apiURL}/jobs/${encodeURIComponent(jobId)}`));
    } catch (e: any) {
      throw new Error(mensagemJson(e, 'Falha ao consultar o status do relatório.'));
    }
  }

  async baixarJob(jobId: string): Promise<ArquivoGerado> {
    try {
      const r = await firstValueFrom(this.http.get(`${this.apiURL}/download/${encodeURIComponent(jobId)}`, {
        observe: 'response',
        responseType: 'blob',
      }));
      return arquivoDa(r);
    } catch (e: any) {
      throw new Error(await mensagemDoBlob(e, padraoDoErro(e, 'Falha ao baixar o relatório.')));
    }
  }
}

function arquivoDa(r: HttpResponse<Blob>): ArquivoGerado {
  const contentType = r.headers.get('Content-Type') ?? r.body?.type ?? '';
  const padrao = ehPdf(contentType, '') ? 'relatorio.pdf' : 'relatorio.xlsx';
  return { blob: r.body as Blob, contentType, nome: nomeDoArquivo(r.headers.get('Content-Disposition'), padrao) };
}

/** O ingress corta a requisição longa e responde HTML (sem CORS o navegador vê status 0): sem mensagem no corpo, vale o aviso de tempo. */
function padraoDoErro(e: any, padrao: string): string {
  return [0, 408, 502, 503, 504].includes(e?.status) ? TEMPO_ESGOTADO : padrao;
}

function mensagemJson(e: any, padrao: string): string {
  const c = e?.error;
  return c?.error ?? c?.message ?? c?.notifications?.[0]?.message ?? padraoDoErro(e, padrao);
}
